import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { getAuthToken } from "lib/providers";
import { fetchWithMediaCache } from "lib/mediaCache";
import { downloadFile, filenameFromUrl } from "lib/shareFile";
import { useTranslation } from "locales";
import "./style.scss";

interface Props {
  src: string;
  fileName: string;
  mediaType: string;
  onClose: () => void;
}

function MediaPreviewModal({ src, fileName, mediaType, onClose }: Props) {
  const { t } = useTranslation();
  const isVideo = mediaType.startsWith("video");
  const isAudio = mediaType.startsWith("audio");
  const isImage = mediaType.startsWith("image");

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAuthToken();
        if (cancelled) return;
        const contentType = isVideo ? "video/mp4" : isAudio ? "audio/mpeg" : undefined;
        const url = await fetchWithMediaCache(src, token ? { Authorization: token } : {}, contentType);
        if (!cancelled) setBlobUrl(url);
      } catch (e) {
        console.error("MediaPreviewModal: failed to load media:", e);
        if (!cancelled) setLoadError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [src, mediaType]);

  useEffect(() => {
    return () => {
      if (blobUrl) setTimeout(() => URL.revokeObjectURL(blobUrl), 3000);
    };
  }, [blobUrl]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [handleKeyDown]);

  const handleBackdrop = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  // Download from the already-fetched blob: instant, no re-download (matters
  // for large media past the 5 MB cache), and works on web/desktop/mobile —
  // a plain `<a download>` on a blob: URL is a no-op in Capacitor WKWebView.
  const handleDownload = useCallback(async () => {
    if (!blobUrl || downloading) return;
    setDownloading(true);
    // blobUrl's path is a random UUID — derive the name from the real `src`
    // (extensionless storage path) with a mediaType-based extension fallback.
    const ext = (mediaType.split("/")[1] || "bin").split("+")[0];
    const name = fileName || filenameFromUrl(src, `file.${ext}`);
    try {
      await downloadFile(blobUrl, { filename: name, mimeType: mediaType, title: name });
    } catch (e) {
      console.error("MediaPreviewModal: download failed:", e);
    } finally {
      if (mountedRef.current) setDownloading(false);
    }
  }, [blobUrl, fileName, mediaType, downloading, src]);

  return ReactDOM.createPortal(
    <div className="media-preview-modal" onClick={handleBackdrop}>
      <button className="media-preview-modal__close" onClick={onClose} type="button">
        <svg viewBox="0 0 24 24" width="22" height="22">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor" />
        </svg>
      </button>

      <div className="media-preview-modal__content" onClick={(e) => e.stopPropagation()}>
        {loadError ? (
          <div className="media-preview-modal__error">{t("Failed to load file")}</div>
        ) : !blobUrl ? (
          <div className="media-preview-modal__loading">
            <span className="media-preview-modal__spinner" />
          </div>
        ) : isVideo ? (
          <video src={blobUrl} controls autoPlay playsInline className="media-preview-modal__video" />
        ) : isAudio ? (
          <div className="media-preview-modal__audio-wrap">
            <div className="media-preview-modal__audio-name">{fileName}</div>
            <audio src={blobUrl} controls autoPlay className="media-preview-modal__audio" />
          </div>
        ) : isImage ? (
          <img src={blobUrl} alt={fileName} className="media-preview-modal__image" />
        ) : (
          <iframe src={blobUrl} title={fileName} className="media-preview-modal__iframe" />
        )}
      </div>

      {blobUrl && (
        <div className="media-preview-modal__footer">
          <span className="media-preview-modal__filename">{fileName}</span>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="media-preview-modal__download"
            title={t("Download")}
          >
            {downloading ? (
              <span className="media-preview-modal__spinner media-preview-modal__spinner--sm" />
            ) : (
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor" />
              </svg>
            )}
          </button>
        </div>
      )}
    </div>,
    document.body
  );
}

export default React.memo(MediaPreviewModal);
